import { useState, useEffect, useMemo, useId } from "react";
import Icon from "../icons/Icon";
import Modal from "../modals/Modal";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import { useRoles } from "../../hooks/useRoles";
import { countAccessLevels } from "../../config/matrix";
import "../../styles/components/settings-pages.css";
import "../../styles/components/team-page.css";

const functions = getFunctions(undefined, "asia-southeast1");

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "viewer", label: "Viewer" },
];

/**
 * Compound select-value encoding for the role dropdown.
 * Predefined: "admin" | "viewer" | "superuser"
 * Custom:     "custom:<roleId>"
 */
function encodeRoleValue(role, customRoleId) {
  if (role === "custom" && customRoleId) return `custom:${customRoleId}`;
  return role || "admin";
}

function decodeRoleValue(value) {
  if (value && value.startsWith("custom:")) {
    return { role: "custom", customRoleId: value.slice("custom:".length) };
  }
  return { role: value, customRoleId: null };
}

const STATUS_ORDER = { active: 0, pending: 1, deactivated: 2, revoked: 3 };

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function sortUsers(users) {
  return [...users].sort((a, b) => {
    // Owner first
    if (a.isOwner && !b.isOwner) return -1;
    if (!a.isOwner && b.isOwner) return 1;
    // Then by status order
    const sa = STATUS_ORDER[a.status] ?? 9;
    const sb = STATUS_ORDER[b.status] ?? 9;
    if (sa !== sb) return sa - sb;
    // Then alphabetically
    return (a.fullName || "").localeCompare(b.fullName || "");
  });
}

const TeamPage = () => {
  const { company, hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const { roles: customRoles, resolveRoleName } = useRoles(company?.id);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [copyLinkData, setCopyLinkData] = useState(null);
  const inviteTitleId = useId();
  const editTitleId = useId();
  const confirmTitleId = useId();

  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Add form. roleValue uses compound encoding (see encodeRoleValue).
  const [addForm, setAddForm] = useState({
    email: "",
    fullName: "",
    roleValue: "admin",
  });

  // Edit form value (compound encoding).
  const [editRoleValue, setEditRoleValue] = useState("admin");

  // Retry key — increment to force onSnapshot re-subscribe on error
  const [retryKey, setRetryKey] = useState(0);

  const callerRole = user?.userData?.role;
  const canManageRoles = hasPermission("set:roles:edit");

  // Predefined role options with superuser (only if caller is superuser).
  const predefinedRoleOptions =
    callerRole === "superuser"
      ? [{ value: "superuser", label: "Superuser" }, ...ROLE_OPTIONS]
      : ROLE_OPTIONS;

  // Set of known custom role IDs — used only for the Edit User modal's
  // "Unknown" optgroup branch (existence check, not label lookup).
  // Display labels go through `resolveRoleName` from the hook.
  const knownCustomRoleIds = useMemo(
    () => new Set(customRoles.map((r) => r.id)),
    [customRoles],
  );

  const getRoleLabel = (u) => {
    if (u.role === "custom") return resolveRoleName(u.customRoleId);
    return u.role || "admin";
  };

  // Real-time listener — auto-updates when user docs change (e.g. pending → active).
  // Defensive guard: route gating already restricts this page, but the
  // retry mechanism (`retryKey`) below could amplify a permission-denied
  // failure into a tight loop. Block subscription entirely when the
  // caller lacks the read permission so retry can't engage.
  useEffect(() => {
    if (!company?.id) return;
    if (!hasPermission("set:team:view")) {
      setLoading(false);
      return;
    }
    const unsub = onSnapshot(
      collection(db, "companies", company.id, "users"),
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setUsers(sortUsers(list));
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Error listening to users:", err);
        setError("Failed to load team members.");
        setLoading(false);
      }
    );
    return unsub;
  }, [company?.id, retryKey, hasPermission]);


  // Filtering
  // - searchQuery matches name, email, OR resolved role label
  // - roleFilter values: "all" | "superuser" | "admin" | "viewer" | "custom:<roleId>"
  const filtered = users.filter((u) => {
    const q = searchQuery.trim().toLowerCase();
    const roleLabel = getRoleLabel(u).toLowerCase();
    const matchesSearch =
      !q ||
      (u.fullName || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      roleLabel.includes(q);

    let matchesRole = true;
    if (roleFilter !== "all") {
      if (roleFilter.startsWith("custom:")) {
        const filterRoleId = roleFilter.slice("custom:".length);
        matchesRole = u.role === "custom" && u.customRoleId === filterRoleId;
      } else {
        matchesRole = u.role === roleFilter;
      }
    }

    return matchesSearch && matchesRole;
  });

  const activeCount = users.filter(
    (u) => u.status === "active" || u.status === "pending"
  ).length;

  // ─── Actions ───────────────────────────────────────────

  const handleInvite = async () => {
    setActionError(null);
    setActionLoading(true);
    try {
      const { role, customRoleId } = decodeRoleValue(addForm.roleValue);
      const payload = {
        email: addForm.email.trim(),
        fullName: addForm.fullName,
        role,
      };
      if (customRoleId) payload.customRoleId = customRoleId;
      const result = await httpsCallable(functions, "inviteUser")(payload);
      setShowAddModal(false);
      setAddForm({ email: "", fullName: "", roleValue: "admin" });
      setCopyLinkData({
        setupLink: result.data.setupLink,
        userName: addForm.fullName,
      });

    } catch (err) {
      setActionError(err.message || "Failed to invite user.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResend = async (targetUser) => {
    setActionError(null);
    setActionLoading(true);
    try {
      const result = await httpsCallable(
        functions,
        "resendInvite"
      )({ userDocId: targetUser.id });
      setCopyLinkData({
        setupLink: result.data.setupLink,
        userName: targetUser.fullName,
      });
    } catch (err) {
      setActionError(err.message || "Failed to resend invite.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!confirmAction?.user) return;
    setActionError(null);
    setActionLoading(true);
    try {
      await httpsCallable(
        functions,
        "revokeInvite"
      )({ userDocId: confirmAction.user.id });
      setConfirmAction(null);

    } catch (err) {
      setActionError(err.message || "Failed to revoke invite.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!confirmAction?.user) return;
    setActionError(null);
    setActionLoading(true);
    try {
      await httpsCallable(
        functions,
        "deactivateUser"
      )({ userDocId: confirmAction.user.id });
      setConfirmAction(null);
      setEditingUser(null);

    } catch (err) {
      setActionError(err.message || "Failed to deactivate user.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editingUser) return;
    setActionError(null);
    setActionLoading(true);
    try {
      const { role, customRoleId } = decodeRoleValue(editRoleValue);
      const payload = { userDocId: editingUser.id, newRole: role };
      if (customRoleId) payload.customRoleId = customRoleId;
      await httpsCallable(functions, "updateUserRole")(payload);
      setEditingUser(null);

    } catch (err) {
      setActionError(err.message || "Failed to update role.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReinvite = async (targetUser) => {
    setActionError(null);
    setActionLoading(true);
    try {
      const payload = {
        email: targetUser.email,
        fullName: targetUser.fullName,
        role: targetUser.role || "admin",
      };
      if (targetUser.role === "custom" && targetUser.customRoleId) {
        payload.customRoleId = targetUser.customRoleId;
      }
      const result = await httpsCallable(functions, "inviteUser")(payload);
      setCopyLinkData({
        setupLink: result.data.setupLink,
        userName: targetUser.fullName,
      });

    } catch (err) {
      setActionError(err.message || "Failed to re-invite user.");
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Render helpers ────────────────────────────────────

  const renderRoleBadge = (u) => {
    const cls = u.role || "admin";
    const label = getRoleLabel(u);
    const isUnknown =
      u.role === "custom" && !knownCustomRoleIds.has(u.customRoleId);
    return (
      <span
        className={`tm-role-badge tm-role-badge--${cls}${isUnknown ? " tm-role-badge--unknown" : ""}`}
        title={isUnknown ? "This user's custom role no longer exists. Edit to reassign." : undefined}
      >
        {label}
      </span>
    );
  };

  const renderStatusDot = (status) => (
    <span
      className={`tm-status-dot tm-status-dot--${status || "active"}`}
      title={status}
    />
  );

  const currentUserDocId = user?.userData?.userDocId;

  const renderActions = (u) => {
    if (u.isOwner) {
      return <span className="tm-owner-label">Owner</span>;
    }

    // Don't show Edit/Deactivate on your own row
    if (u.id === currentUserDocId) {
      return <span className="tm-owner-label">You</span>;
    }

    const actions = [];

    if (u.status === "active") {
      if (hasPermission("set:team:edit")) {
        actions.push(
          <button
            key="edit"
            className="tm-action-btn"
            onClick={() => {
              setEditingUser(u);
              setEditRoleValue(encodeRoleValue(u.role, u.customRoleId));
              setActionError(null);
            }}
          >
            Edit
          </button>
        );
      }
      if (hasPermission("set:team:edit")) {
        actions.push(
          <button
            key="deact"
            className="tm-action-btn tm-action-btn--danger"
            onClick={() => {
              setConfirmAction({ type: "deactivate", user: u });
              setActionError(null);
            }}
          >
            Deactivate
          </button>
        );
      }
    }

    if (u.status === "pending") {
      if (hasPermission("set:team:edit")) {
        actions.push(
          <button
            key="resend"
            className="tm-action-btn tm-action-btn--primary"
            onClick={() => handleResend(u)}
            disabled={actionLoading}
          >
            Resend
          </button>
        );
      }
      if (hasPermission("set:team:edit")) {
        actions.push(
          <button
            key="revoke"
            className="tm-action-btn tm-action-btn--danger"
            onClick={() => {
              setConfirmAction({ type: "revoke", user: u });
              setActionError(null);
            }}
          >
            Revoke
          </button>
        );
      }
    }

    if (u.status === "deactivated" || u.status === "revoked") {
      if (hasPermission("set:team:edit")) {
        actions.push(
          <button
            key="reinvite"
            className="tm-action-btn tm-action-btn--primary"
            onClick={() => handleReinvite(u)}
            disabled={actionLoading}
          >
            Re-invite
          </button>
        );
      }
    }

    return actions.length > 0 ? actions : null;
  };

  // ─── Main render ───────────────────────────────────────

  return (
    <div>
      <h1 className="settings-page-title">Team Members</h1>

      {/* Controls */}
      <div className="tm-controls">
        <input
          type="text"
          className="tm-search-input"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="tm-role-filter"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">All Roles</option>
          <optgroup label="Predefined">
            <option value="superuser">Superuser</option>
            <option value="admin">Admin</option>
            <option value="viewer">Viewer</option>
          </optgroup>
          {customRoles.length > 0 && (
            <optgroup label="Custom Roles">
              {customRoles.map((r) => (
                <option key={r.id} value={`custom:${r.id}`}>
                  {r.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        {hasPermission("set:team:edit") && (
          <>
            <div className="tm-divider" />
            <button
              className="tm-add-btn"
              onClick={() => {
                setShowAddModal(true);
                setActionError(null);
              }}
            >
              <Icon name="plus" size={14} />
              Add User
            </button>
          </>
        )}
      </div>

      {/* User List */}
      <div className="tm-card">
        {loading ? (
          <div className="tm-loading">
            <div className="tm-loading__spinner" />
            Loading team members...
          </div>
        ) : error ? (
          <div className="tm-error">
            <div className="tm-error__icon">!</div>
            <div className="tm-error__title">Error</div>
            <div className="tm-error__text">{error}</div>
            <button className="tm-retry-btn" onClick={() => { setLoading(true); setRetryKey((k) => k + 1); }}>
              Try Again
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="tm-empty">
            <div className="tm-empty__icon">
              {users.length === 0 ? <Icon name="users" size={28} /> : <Icon name="search" size={28} />}
            </div>
            <div className="tm-empty__title">
              {users.length === 0 ? "No team members yet" : "No matches found"}
            </div>
            <div className="tm-empty__text">
              {users.length === 0
                ? "Invite your first team member to get started."
                : "Try adjusting your search or filter."}
            </div>
            {users.length === 0 && hasPermission("set:team:edit") && (
              <button
                className="tm-add-btn"
                style={{ margin: "0 auto" }}
                onClick={() => {
                  setShowAddModal(true);
                  setActionError(null);
                }}
              >
                Add User
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="tm-results-count">
              Showing {filtered.length} of {users.length} users
            </div>

            {filtered.map((u) => (
              <div
                key={u.id}
                className={`tm-user-row ${
                  u.status === "deactivated" || u.status === "revoked"
                    ? "tm-user-row--deactivated"
                    : ""
                }`}
              >
                <div className="tm-avatar">{getInitials(u.fullName)}</div>
                <div className="tm-user-info">
                  <div className="tm-user-name">{u.fullName || "—"}</div>
                  <div className="tm-user-email">{u.email || "—"}</div>
                </div>
                <div className="tm-badges">
                  {renderRoleBadge(u)}
                  {renderStatusDot(u.status)}
                </div>
                <div className="tm-actions">{renderActions(u)}</div>
              </div>
            ))}

            <div className="tm-footer">
              <div className="tm-footer-stat">
                <span className="tm-footer-stat__label">Users</span>
                <span className="tm-footer-stat__value">
                  {activeCount} / 10
                </span>
              </div>
              <span className="tm-footer-hint">
                {activeCount >= 10
                  ? "User limit reached"
                  : `${10 - activeCount} seats available`}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ─── Add User Modal ──────────────────────────── */}
      {showAddModal && (
        <Modal isOpen onClose={() => setShowAddModal(false)} busy={actionLoading} labelledBy={inviteTitleId} className="tm-modal">
            <div className="tm-modal__header">
              <h3 className="tm-modal__title" id={inviteTitleId}>Invite User</h3>
            </div>
            <div className="tm-modal__body">
              <div className="tm-form-group">
                <label className="tm-form-label">Email</label>
                <input
                  type="text"
                  inputMode="email"
                  autoCapitalize="none"
                  className="tm-form-input"
                  placeholder="user@example.com"
                  value={addForm.email}
                  // Trimmed when the invite is sent, not on every keystroke:
                  // stripping the space that ends a macOS text replacement
                  // leaves its suggestion open (BL-105).
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, email: e.target.value }))
                  }
                />
              </div>
              <div className="tm-form-group">
                <label className="tm-form-label">Full Name</label>
                <input
                  type="text"
                  className="tm-form-input"
                  placeholder="Juan Dela Cruz"
                  value={addForm.fullName}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, fullName: e.target.value }))
                  }
                />
              </div>
              <div className="tm-form-group">
                <label className="tm-form-label">Role</label>
                <select
                  className="tm-form-select"
                  value={addForm.roleValue}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, roleValue: e.target.value }))
                  }
                >
                  <optgroup label="Predefined">
                    {predefinedRoleOptions.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </optgroup>
                  {customRoles.length > 0 && (
                    <optgroup label="Custom Roles">
                      {customRoles.map((r) => (
                        <option key={r.id} value={`custom:${r.id}`}>
                          {r.name} · {countAccessLevels(r.permissions)} access level{countAccessLevels(r.permissions) === 1 ? "" : "s"}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {canManageRoles && (
                  <button
                    type="button"
                    className="tm-form-link"
                    onClick={() =>
                      navigate(`/company/${company?.slug}/settings/roles/new`)
                    }
                  >
                    + Create new role
                  </button>
                )}
              </div>
              <div className="tm-info-box">
                You'll receive a setup link to share with the invited user. The
                link expires in 1 hour.
              </div>
              {actionError && (
                <div className="tm-form-error" style={{ marginTop: 12 }}>
                  {actionError}
                </div>
              )}
            </div>
            <div className="tm-modal__footer">
              <button
                className="tm-btn tm-btn--cancel"
                onClick={() => setShowAddModal(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="tm-btn tm-btn--primary"
                onClick={handleInvite}
                disabled={
                  actionLoading ||
                  !addForm.email.trim() ||
                  !addForm.fullName.trim()
                }
              >
                {actionLoading ? "Inviting..." : "Invite User"}
              </button>
            </div>
        </Modal>
      )}

      {/* ─── Copy Link Modal ─────────────────────────── */}
      {copyLinkData && <CopyLinkModal data={copyLinkData} onClose={() => setCopyLinkData(null)} />}

      {/* ─── Edit User Modal ─────────────────────────── */}
      {editingUser && (
        <Modal isOpen onClose={() => setEditingUser(null)} busy={actionLoading} labelledBy={editTitleId} className="tm-modal">
            <div className="tm-modal__header">
              <h3 className="tm-modal__title" id={editTitleId}>Edit User</h3>
            </div>
            <div className="tm-modal__body">
              <div className="tm-form-group">
                <label className="tm-form-label">Name</label>
                <input
                  type="text"
                  className="tm-form-input"
                  value={editingUser.fullName || ""}
                  readOnly
                />
              </div>
              <div className="tm-form-group">
                <label className="tm-form-label">Email</label>
                <input
                  type="text"
                  className="tm-form-input"
                  value={editingUser.email || ""}
                  readOnly
                />
              </div>
              <div className="tm-form-group">
                <label className="tm-form-label">Role</label>
                <select
                  className="tm-form-select"
                  value={editRoleValue}
                  onChange={(e) => setEditRoleValue(e.target.value)}
                >
                  <optgroup label="Predefined">
                    {predefinedRoleOptions.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </optgroup>
                  {customRoles.length > 0 && (
                    <optgroup label="Custom Roles">
                      {customRoles.map((r) => (
                        <option key={r.id} value={`custom:${r.id}`}>
                          {r.name} · {countAccessLevels(r.permissions)} access level{countAccessLevels(r.permissions) === 1 ? "" : "s"}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {/* Show the user's current custom role even if it was deleted,
                      so the dropdown matches the current state. */}
                  {editingUser?.role === "custom" &&
                    editingUser?.customRoleId &&
                    !knownCustomRoleIds.has(editingUser.customRoleId) && (
                    <optgroup label="Unknown">
                      <option value={`custom:${editingUser.customRoleId}`} disabled>
                        — Unknown role (deleted) —
                      </option>
                    </optgroup>
                  )}
                </select>
                {canManageRoles && (
                  <button
                    type="button"
                    className="tm-form-link"
                    onClick={() =>
                      navigate(`/company/${company?.slug}/settings/roles/new`)
                    }
                  >
                    + Create new role
                  </button>
                )}
              </div>
              <div className="tm-warning-box">
                Role changes take effect within 1 hour or on next login.
              </div>

              {hasPermission("set:team:edit") && (
                <>
                  <hr className="tm-modal-divider" />
                  <div className="tm-deactivate-section">
                    <div className="tm-deactivate-section__title">
                      Danger Zone
                    </div>
                    <div className="tm-deactivate-section__text">
                      Deactivating this user will immediately revoke their
                      access. You can re-invite them later.
                    </div>
                    <button
                      className="tm-btn tm-btn--danger"
                      onClick={() => {
                        setConfirmAction({
                          type: "deactivate",
                          user: editingUser,
                        });
                        setActionError(null);
                      }}
                      disabled={actionLoading}
                    >
                      Deactivate User
                    </button>
                  </div>
                </>
              )}

              {actionError && (
                <div className="tm-form-error" style={{ marginTop: 12 }}>
                  {actionError}
                </div>
              )}
            </div>
            <div className="tm-modal__footer">
              <button
                className="tm-btn tm-btn--cancel"
                onClick={() => setEditingUser(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="tm-btn tm-btn--primary"
                onClick={handleUpdateRole}
                disabled={
                  actionLoading ||
                  editRoleValue ===
                    encodeRoleValue(editingUser.role, editingUser.customRoleId)
                }
              >
                {actionLoading ? "Saving..." : "Save Role"}
              </button>
            </div>
        </Modal>
      )}

      {/* ─── Confirm Modal (Deactivate / Revoke) — opens over Edit User, so
           the shell's stack closes it first on Esc (C7) ─────── */}
      {confirmAction && (
        <Modal isOpen onClose={() => setConfirmAction(null)} busy={actionLoading} labelledBy={confirmTitleId} className="tm-modal tm-confirm-modal">
            <div className="tm-modal__header">
              <h3 className="tm-modal__title" id={confirmTitleId}>
                {confirmAction.type === "deactivate"
                  ? "Deactivate User"
                  : "Revoke Invitation"}
              </h3>
            </div>
            <div className="tm-modal__body">
              <p className="tm-confirm-text">
                {confirmAction.type === "deactivate"
                  ? `Are you sure you want to deactivate ${confirmAction.user.fullName}? Their access will be revoked immediately.`
                  : `Are you sure you want to revoke the invitation for ${confirmAction.user.fullName}? Their pending account will be removed.`}
              </p>
              {actionError && (
                <div className="tm-form-error" style={{ marginTop: 12 }}>
                  {actionError}
                </div>
              )}
            </div>
            <div className="tm-modal__footer">
              <button
                className="tm-btn tm-btn--cancel"
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="tm-btn tm-btn--danger"
                onClick={
                  confirmAction.type === "deactivate"
                    ? handleDeactivate
                    : handleRevoke
                }
                disabled={actionLoading}
              >
                {actionLoading
                  ? "Processing..."
                  : confirmAction.type === "deactivate"
                  ? "Deactivate"
                  : "Revoke"}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
};

/* ─── Copy Link Sub-Component ───────────────────────── */

const CopyLinkModal = ({ data, onClose }) => {
  const [copied, setCopied] = useState(false);
  const titleId = useId();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(data.setupLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the input text
      const input = document.querySelector(".tm-copy-link-input");
      if (input) {
        input.select();
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  return (
    <Modal isOpen onClose={onClose} labelledBy={titleId} className="tm-modal">
        <div className="tm-modal__header">
          <h3 className="tm-modal__title" id={titleId}>Invite Link Ready</h3>
        </div>
        <div className="tm-modal__body">
          <p style={{ fontSize: 14, color: "var(--ui-text-secondary)", marginBottom: 4 }}>
            Share this link with <strong>{data.userName}</strong> to complete
            their account setup.
          </p>
          <div className="tm-copy-link-area">
            <input
              type="text"
              className="tm-copy-link-input"
              value={data.setupLink}
              readOnly
              onClick={(e) => e.target.select()}
            />
            <button
              className={`tm-copy-btn ${copied ? "tm-copy-btn--copied" : ""}`}
              onClick={handleCopy}
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="tm-link-hint">
            This link expires in 1 hour. If it expires, use the "Resend" button
            to generate a new one.
          </div>
        </div>
        <div className="tm-modal__footer">
          <button className="tm-btn tm-btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
    </Modal>
  );
};

export default TeamPage;
